import { css } from '@emotion/react';
import { faCrosshairs } from 'node_modules/@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ButtonComponent, ButtonSize, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { setModalConfirmedToStorage } from '~/_shared/components/modal/confirmation/confirmationModal.helpers';
import { PermanentConfirmStrategy } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { useStorageService } from '~/_shared/utils/storageService';
import { HIDE_BOUNDARY_ZOOM_DISCLAIMER_SESSION_KEY } from '~/map/map/boundary/boundaryView/mapBoundaryPolygons/useBoundaryZoomDisclaimer';
import type { ModalProps } from '~/modal/modalType.enum';
import { startSelectLocationTool } from '~/store/frontendState/mapTools/selectLocation/selectLocation.actionCreators';
import { SelectLocationConfiguration } from '~/store/frontendState/mapTools/selectLocation/selectLocation.state';

type BoundaryZoomLevelDisclaimerItem = {
  groupName: string;
  zoomInCount: number;
  targetZoomLevel: number;
};

const modalContentStyle = css({
  padding: '20px 25px',
});

const descriptionStyle = css({
  padding: 0,
  margin: 0,
  fontSize: 18,
  fontWeight: 400,
});

const headingStyle = css({
  fontSize: 18,
  fontWeight: 700,
  marginBottom: 15,
  display: 'block',
});

const checkboxStyle = (theme: Theme) => css({
  marginTop: 30,
  fontSize: 12,
  color: theme.textColors.secondary,
});

const listStyle = css({
  listStyle: 'none',
  padding: '0 0 0 0',
  margin: 0,
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 2fr',
  rowGap: 10,
  columnGap: 5,
  alignItems: 'center',
});

const listItemStyle = css({
  display: 'contents',
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.25,
});

const checkboxTextStyle = css({
  marginLeft: 6,
});

const listWrapperStyle = css({
  marginTop: 20,
});

const groupNameStyle = css({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const clicksCountStyle = (theme: Theme) => css({
  whiteSpace: 'nowrap',
  color: theme.textColors.secondary,
});

const clickToZoomButtonStyle = css({
  display: 'flex',
  alignItems: 'center',
});

export type BoundaryZoomLevelDisclaimerProps = ModalProps<{
  items: BoundaryZoomLevelDisclaimerItem[];
}>;

export const BoundaryZoomLevelDisclaimerModalComponent = ({
  items,
  onClose,
  isOpen,
}: BoundaryZoomLevelDisclaimerProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const storageService = useStorageService();

  const [showAgain, setShowAgain] = useState(true);

  const handleClose = () => {
    if (!showAgain) {
      setModalConfirmedToStorage(storageService, {
        id: HIDE_BOUNDARY_ZOOM_DISCLAIMER_SESSION_KEY,
        strategy: PermanentConfirmStrategy.Session,
      });
    }

    onClose();
  };

  const handleZoomTo = (targetZoomLevel: number) => {
    dispatch(startSelectLocationTool(
      SelectLocationConfiguration.ZoomToLocation,
      { value: targetZoomLevel }
    ));
    handleClose();
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      caption={t('Zooming required to see boundary')}
      contentStyle={modalContentStyle}
    >
      <p css={descriptionStyle}>{t('At this map zoom level, some boundaries are not visible on the map. To see your boundaries you must zoom in a few clicks.')}</p>
      <div css={listWrapperStyle}>
        <strong css={headingStyle}>{t('Boundaries requiring zoom')}</strong>
        <OverlayScrollbarComponent
          type={ScrollbarType.Vertical}
          maxHeight={80}
        >
          <ul css={listStyle}>
            {items.map((item, index) => (
              <li css={listItemStyle} key={index}>
                <TooltipComponent
                  tooltipContent={item.groupName}
                  behavior={TooltipBehavior.ShowOnOverflow}
                >
                  <span css={groupNameStyle}>
                    {item.groupName}
                  </span>
                </TooltipComponent>
                <span css={clicksCountStyle}>
                  {t('{{count}} clicks', {
                    count: item.zoomInCount,
                  })}
                </span>
                <span css={clickToZoomButtonStyle}>
                  <ButtonComponent
                    size={ButtonSize.Small}
                    buttonStyle={ButtonStyle.Success}
                    text={t('Zoom to Location')}
                    prefixIcon={faCrosshairs}
                    onClick={() => handleZoomTo(item.targetZoomLevel)}
                  />
                </span>
              </li>
            ))}
          </ul>
        </OverlayScrollbarComponent>

        <CheckboxComponent
          css={checkboxStyle}
          textStyle={checkboxTextStyle}
          checkedSetter={() => {
            setShowAgain(!showAgain);
          }}
          isChecked={!showAgain}
          text={t('Do not show again for this session')}
        />
      </div>
    </ModalComponent>
  );
};
